// client/src/components/world/InstancedModules.jsx
// Итерация 8.2: instancedMesh для больших палуб (>= 50 модулей).
//
// Логика:
//   1. Группировка модулей по ключу (primitive_type + args + color + emissive).
//   2. Для каждой группы — <instancedMesh> с matrix из position/rotation.
//   3. currentModuleId — отдельный <ModulePrimitive highlight />.
//
// Преимущества:
//   - ≤ 11 draw calls (по числу primitive_type) вместо 500.
//   - FPS ≥ 30 на планшете.
//
// ⚠️ Не знает про палубы/радиусы — получает готовый массив modules.

import React, { useMemo, useRef, useLayoutEffect } from 'react';
import * as THREE from 'three';
import ModulePrimitive from './ModulePrimitive';
import { getModuleGeometry } from '../../utils/moduleGeometry';

// ============================================================================
// ГРУППИРОВКА
// ============================================================================

function groupModules(modules) {
  const groups = new Map();

  for (const module of modules) {
    const geom = getModuleGeometry(module);
    const key = buildGroupKey(geom);

    if (!groups.has(key)) {
      groups.set(key, {
        geom,
        modules: [],
      });
    }

    groups.get(key).modules.push(module);
  }

  return groups;
}

function buildGroupKey(geom) {
  const args = geom.args.join(',');
  const color = geom.material.color;
  const emissive = geom.material.emissive || 'none';
  return `${geom.type}|${args}|${color}|${emissive}`;
}

// ============================================================================
// ОДНА ГРУППА (instancedMesh)
// ============================================================================

function InstancedGroup({ geom, modules }) {
  const meshRef = useRef();

  useLayoutEffect(() => {
    if (!meshRef.current) return;
    const dummy = new THREE.Object3D();
    const deg2rad = Math.PI / 180;

    modules.forEach((module, idx) => {
      const p = module.position;
      dummy.position.set(p.x, p.y, p.z);

      const r = module.rotation;
      if (
        r &&
        typeof r.x === 'number' &&
        typeof r.y === 'number' &&
        typeof r.z === 'number'
      ) {
        dummy.rotation.set(r.x * deg2rad, r.y * deg2rad, r.z * deg2rad);
      } else {
        dummy.rotation.set(0, 0, 0);
      }

      dummy.updateMatrix();
      meshRef.current.setMatrixAt(idx, dummy.matrix);
    });

    meshRef.current.instanceMatrix.needsUpdate = true;
    meshRef.current.count = modules.length;
  }, [modules, geom]);

  const materialProps = {
    color: geom.material.color,
    emissive: geom.material.emissive || '#000000',
    emissiveIntensity: geom.material.emissiveIntensity || 0,
    metalness: geom.material.metalness ?? 0.5,
    roughness: geom.material.roughness ?? 0.5,
  };

  return (
    <instancedMesh
      ref={meshRef}
      args={[undefined, undefined, modules.length]}
      castShadow
      receiveShadow
    >
      {renderGeometryFromGeom(geom)}
      <meshStandardMaterial {...materialProps} />
    </instancedMesh>
  );
}

function renderGeometryFromGeom(geom) {
  switch (geom.type) {
    case 'box':
      return <boxGeometry args={geom.args} />;
    case 'sphere':
      return <sphereGeometry args={geom.args} />;
    case 'cylinder':
      return <cylinderGeometry args={geom.args} />;
    case 'cone':
      return <coneGeometry args={geom.args} />;
    case 'torus':
      return <torusGeometry args={geom.args} />;
    default:
      return <boxGeometry args={[2, 2, 2]} />;
  }
}

// ============================================================================
// ГЛАВНЫЙ КОМПОНЕНТ
// ============================================================================

export default function InstancedModules({ modules, currentModuleId = null }) {
  const { groups, highlightModule, otherModules } = useMemo(() => {
    if (!Array.isArray(modules) || modules.length === 0) {
      return { groups: new Map(), highlightModule: null, otherModules: [] };
    }

    // Отделяем currentModuleId (рендерим отдельно — highlight)
    let highlightModule = null;
    const otherModules = [];

    for (const m of modules) {
      const id = m.instance_id || m.id;
      if (currentModuleId && id === currentModuleId) {
        highlightModule = m;
      } else {
        otherModules.push(m);
      }
    }

    return {
      groups: groupModules(otherModules),
      highlightModule,
      otherModules,
    };
  }, [modules, currentModuleId]);

  if (!modules || modules.length === 0) {
    return null;
  }

  return (
    <group name="interior-modules-instanced">
      {Array.from(groups.entries()).map(([key, group]) => (
        <InstancedGroup
          key={key}
          geom={group.geom}
          modules={group.modules}
        />
      ))}

      {highlightModule && (
        <ModulePrimitive module={highlightModule} highlight={true} />
      )}
    </group>
  );
}
